import AcUnitIcon from "@mui/icons-material/AcUnit";
import AttachFileIcon from "@mui/icons-material/AttachFile";
import BugReportIcon from "@mui/icons-material/BugReport";
import CallIcon from "@mui/icons-material/Call";
import DashboardIcon from "@mui/icons-material/Dashboard";
import DeleteIcon from "@mui/icons-material/Delete";
import DownloadIcon from "@mui/icons-material/Download";
import DraftsIcon from "@mui/icons-material/Drafts";
import EditIcon from "@mui/icons-material/Edit";
import EmailIcon from "@mui/icons-material/Email";
import EventIcon from "@mui/icons-material/Event";
import HowToVoteIcon from "@mui/icons-material/HowToVote";
import LinkedInIcon from "@mui/icons-material/LinkedIn";
import MarkunreadIcon from "@mui/icons-material/Markunread";
import NoteIcon from "@mui/icons-material/Note";
import PersonAddIcon from "@mui/icons-material/PersonAdd";
import ReplyIcon from "@mui/icons-material/Reply";
import ReportIcon from "@mui/icons-material/Report";
import SmartToyIcon from "@mui/icons-material/SmartToy";
import SmsFailedIcon from "@mui/icons-material/SmsFailed";
import VisibilityIcon from "@mui/icons-material/Visibility";
import WorkIcon from "@mui/icons-material/Work";
import { TimelineDot } from "@mui/lab";
import {
	Box,
	Button,
	Card,
	Chip,
	IconButton,
	Link,
	Stack,
	Tooltip,
	Typography,
} from "@mui/material";
import type { Theme } from "@mui/material/styles";
import { useState } from "react";
import type { Comment } from "../../../api/comments/comments";
import { messageDocumentDownloadUrl } from "../../../api/documents/message-documents";
import type { PersonActivity } from "../../../api/person/activity";
import type { PersonEmailMessage } from "../../../api/person/emails";
import type { PersonAttachment } from "../../../api/person/person-attachment";
import type { PersonVote } from "../../../api/votes";
import { triggerAttachmentDownload } from "../../../utils/attachment-download";
import { formatDateTime } from "../../../utils/date-formatting";
import type { ViewerAttachment } from "../../attachment-viewer/attachment-viewer";
import { AssignmentStatusChip } from "../../shared/assignment-status-chip";
import { AttachmentBlobDebug } from "../person-attachment-debug";
import { getEntryChipProps } from "./entry-chip-props";
import type { ReplyContext } from "./reply-dialog";
import { createEmailReplyContext, createLinkedInReplyContext } from "./reply-dialog";
import type { ActivityEntry } from "./timeline-types";
import {
	getEntryDates,
	getEntryDebugId,
	getEntrySender,
	getEntrySubject,
	getEntryText,
} from "./timeline-utils";

interface TimelineEntryDotProps {
	entry: ActivityEntry;
	theme: Theme;
}

/**
 * Timeline dot with appropriate icon and color based on entry type
 */
export const TimelineEntryDot = ({ entry, theme }: TimelineEntryDotProps) => {
	const getIconAndColor = (): { icon: React.ReactNode; color: string } => {
		switch (entry.type) {
			case "linkedin_queued":
			case "linkedin_sent":
			case "linkedin_conversation":
				return {
					icon: <LinkedInIcon sx={{ fontSize: 16 }} />,
					color: theme.palette.primary.main,
				};
			case "email_queued":
			case "email_sent":
			case "email_journal":
				return {
					icon: <EmailIcon sx={{ fontSize: 16 }} />,
					color: theme.palette.success.main,
				};
			case "event":
				return {
					icon: <PersonAddIcon sx={{ fontSize: 16 }} />,
					color: theme.palette.info.main,
				};
			case "vote":
				return {
					icon: <HowToVoteIcon sx={{ fontSize: 16 }} />,
					color: theme.palette.secondary.main,
				};
			case "board_movement":
				return {
					icon: <DashboardIcon sx={{ fontSize: 16 }} />,
					color: theme.palette.board.main,
				};
			case "note":
				return {
					icon: <NoteIcon sx={{ fontSize: 16 }} />,
					color: theme.palette.warning.main,
				};
			case "attachment":
				return {
					icon: <AttachFileIcon sx={{ fontSize: 16 }} />,
					color: theme.palette.warning.main,
				};
			case "call":
				return {
					icon: <CallIcon sx={{ fontSize: 16 }} />,
					color: theme.palette.info.dark,
				};
			case "meeting":
				return {
					icon: <EventIcon sx={{ fontSize: 16 }} />,
					color: theme.palette.secondary.dark,
				};
			case "job_application":
				return {
					icon: <WorkIcon sx={{ fontSize: 16 }} />,
					color: theme.palette.success.main,
				};
			case "cooldown":
				return {
					icon: <AcUnitIcon sx={{ fontSize: 16 }} />,
					color: theme.palette.info.main,
				};
		}
	};

	const { icon, color } = getIconAndColor();
	return (
		<TimelineDot sx={{ bgcolor: color, m: 0, p: 0.5 }} data-component="entry-dot">
			{icon}
		</TimelineDot>
	);
};

interface TimelineEntryContentProps {
	entry: ActivityEntry;
	theme: Theme;
	showDebugIds: boolean;
	currentUsername?: string;
	onDebugClick: (email: PersonEmailMessage) => void;
	onGenericDebugClick: (entry: ActivityEntry) => void;
	onVoteEditClick?: (vote: PersonVote) => void;
	onReplyClick?: (context: ReplyContext) => void;
	onAttachmentDelete?: (attachment: PersonAttachment) => void;
	onAttachmentView?: (attachment: ViewerAttachment) => void;
	onEmailQueueDelete?: (id: number) => void;
	onLinkedinQueueDelete?: (id: number) => void;
	onActivityDelete?: (activityId: number) => void;
	onNoteDelete?: (noteId: number) => void;
	onActivityEdit?: (activity: PersonActivity) => void;
	onNoteEdit?: (note: Comment) => void;
	currentUserId?: number;
	isAdmin?: boolean;
}

/**
 * Timeline entry content card with all the details
 */
export const TimelineEntryContent = ({
	entry,
	theme,
	showDebugIds,
	currentUsername,
	onDebugClick,
	onGenericDebugClick,
	onVoteEditClick,
	onReplyClick,
	onAttachmentDelete,
	onAttachmentView,
	onEmailQueueDelete,
	onLinkedinQueueDelete,
	onActivityDelete,
	onNoteDelete,
	onActivityEdit,
	onNoteEdit,
	currentUserId,
	isAdmin,
}: TimelineEntryContentProps) => {
	const [expanded, setExpanded] = useState(false);

	const chipProps = getEntryChipProps(entry, theme);
	const text = getEntryText(entry);
	const subject = getEntrySubject(entry);
	const sender = getEntrySender(entry);
	const entryDates = getEntryDates(entry);
	const primaryDisplay =
		entryDates.find((d) => d.primary)?.display ?? formatDateTime(entry.date);

	const isEmailType =
		entry.type === "email_journal" ||
		entry.type === "email_queued" ||
		entry.type === "email_sent";
	const isExpandable = (isEmailType && subject) || text.length > 350;

	return (
		<Card sx={{ p: 1.5 }} data-component="entry-content">
			<Stack
				direction="row"
				alignItems="center"
				spacing={1}
				flexWrap="wrap"
				sx={{ mb: 0.5 }}
			>
				<Tooltip title={chipProps.tooltip ?? ""}>
					<Chip
						label={chipProps.label}
						size="small"
						color={chipProps.color}
						variant="outlined"
						sx={{
							height: 22,
							...(chipProps.customColor && {
								borderColor: chipProps.customColor,
								color: chipProps.customColor,
							}),
						}}
						data-component="entry-type-chip"
					/>
				</Tooltip>
				{/* The role's standing now, so a reply can be answered without
				    opening the role. Absent for a vote on no role or a deleted one. */}
				{entry.type === "vote" &&
					entry.data.assignment_status !== null &&
					entry.data.assignment_status_display !== null && (
						<AssignmentStatusChip
							status={entry.data.assignment_status}
							label={entry.data.assignment_status_display}
						/>
					)}
				{/* Email status icons */}
				{entry.type === "email_journal" && (
					<EmailStatusIcons
						entry={entry}
						showDebugIds={showDebugIds}
						onDebugClick={onDebugClick}
					/>
				)}
				{(entry.type === "email_queued" || entry.type === "email_sent") && (
					<EmailQueueStatusIcons entry={entry} showDebugIds={showDebugIds} />
				)}
				{entry.type === "email_queued" && onEmailQueueDelete && (
					<Tooltip title="Delete queued email">
						<IconButton
							size="small"
							color="error"
							onClick={(e) => {
								e.stopPropagation();
								onEmailQueueDelete(entry.data.id);
							}}
							sx={{ p: 0.25 }}
							data-component="email-queue-delete-button"
						>
							<DeleteIcon sx={{ fontSize: 16 }} />
						</IconButton>
					</Tooltip>
				)}
				{entry.type === "linkedin_queued" && onLinkedinQueueDelete && (
					<Tooltip title="Delete queued LinkedIn message">
						<IconButton
							size="small"
							color="error"
							onClick={(e) => {
								e.stopPropagation();
								onLinkedinQueueDelete(entry.data.id);
							}}
							sx={{ p: 0.25 }}
							data-component="linkedin-queue-delete-button"
						>
							<DeleteIcon sx={{ fontSize: 16 }} />
						</IconButton>
					</Tooltip>
				)}
				{/* Vote edit button - always visible for votes */}
				{entry.type === "vote" && onVoteEditClick && (
					<Tooltip title="Edit Vote">
						<IconButton
							size="small"
							onClick={(e) => {
								e.stopPropagation();
								onVoteEditClick(entry.data);
							}}
							sx={{ p: 0.25 }}
							data-component="vote-edit-button"
						>
							<EditIcon sx={{ fontSize: 16 }} />
						</IconButton>
					</Tooltip>
				)}
				{/* Debug icons for other types */}
				{(entry.type === "linkedin_queued" ||
					entry.type === "linkedin_sent" ||
					entry.type === "linkedin_conversation" ||
					entry.type === "event" ||
					entry.type === "vote" ||
					entry.type === "board_movement") &&
					showDebugIds && (
						<Tooltip title="Open Debug View">
							<IconButton
								size="small"
								onClick={(e) => {
									e.stopPropagation();
									onGenericDebugClick(entry);
								}}
								sx={{ p: 0.25 }}
								data-component="generic-debug-button"
							>
								<BugReportIcon sx={{ fontSize: 16 }} />
							</IconButton>
						</Tooltip>
					)}
				{/* Edit + Delete buttons for call/meeting (owner or admin) */}
				{(entry.type === "call" || entry.type === "meeting") &&
					(isAdmin || entry.data.user === currentUserId) && (
						<>
							{onActivityEdit && (
								<Tooltip title={`Edit ${entry.type}`}>
									<IconButton
										size="small"
										onClick={(e) => {
											e.stopPropagation();
											onActivityEdit(entry.data);
										}}
										sx={{ p: 0.25 }}
										data-component="activity-edit-button"
									>
										<EditIcon sx={{ fontSize: 16 }} />
									</IconButton>
								</Tooltip>
							)}
							{onActivityDelete && (
								<Tooltip title={`Delete ${entry.type}`}>
									<IconButton
										size="small"
										color="error"
										onClick={(e) => {
											e.stopPropagation();
											onActivityDelete(entry.data.id);
										}}
										sx={{ p: 0.25 }}
										data-component="activity-delete-button"
									>
										<DeleteIcon sx={{ fontSize: 16 }} />
									</IconButton>
								</Tooltip>
							)}
						</>
					)}
				{/* Edit + Delete buttons for note (owner or admin) */}
				{entry.type === "note" &&
					(isAdmin || entry.data.user_id === currentUserId) && (
						<>
							{onNoteEdit && (
								<Tooltip title="Edit note">
									<IconButton
										size="small"
										onClick={(e) => {
											e.stopPropagation();
											onNoteEdit(entry.data);
										}}
										sx={{ p: 0.25 }}
										data-component="note-edit-button"
									>
										<EditIcon sx={{ fontSize: 16 }} />
									</IconButton>
								</Tooltip>
							)}
							{onNoteDelete && (
								<Tooltip title="Delete note">
									<IconButton
										size="small"
										color="error"
										onClick={(e) => {
											e.stopPropagation();
											onNoteDelete(entry.data.id);
										}}
										sx={{ p: 0.25 }}
										data-component="note-delete-button"
									>
										<DeleteIcon sx={{ fontSize: 16 }} />
									</IconButton>
								</Tooltip>
							)}
						</>
					)}
				<Tooltip
					title={
						entryDates.length > 1 ? (
							<Box data-component="entry-dates-tooltip">
								{entryDates.map((d) => (
									<Typography
										key={d.label}
										variant="caption"
										sx={{
											display: "block",
											fontWeight: d.primary ? 700 : 400,
										}}
									>
										{d.label}: {d.display}
									</Typography>
								))}
							</Box>
						) : (
							""
						)
					}
				>
					<Typography
						variant="caption"
						color="text.secondary"
						data-component="entry-timestamp"
					>
						{primaryDisplay}
					</Typography>
				</Tooltip>
				{sender && (
					<Typography
						variant="caption"
						color="text.secondary"
						data-component="entry-sender"
					>
						{sender.prefix} {sender.name}
					</Typography>
				)}
				{showDebugIds && (
					<Typography
						variant="caption"
						sx={{
							fontFamily: "monospace",
							opacity: 0.5,
							fontSize: "0.65rem",
						}}
						data-component="entry-debug-id"
					>
						{getEntryDebugId(entry)}
					</Typography>
				)}
			</Stack>

			{/* Subject line */}
			{subject && (!isEmailType || expanded) && (
				<Typography
					variant="subtitle2"
					sx={{ fontWeight: 600, mt: 0.5 }}
					data-component="entry-subject"
				>
					{subject}
				</Typography>
			)}

			{/* Email addresses */}
			{entry.type === "email_journal" && expanded && (
				<Typography
					variant="caption"
					color="text.secondary"
					sx={{ display: "block", mt: 0.25 }}
					data-component="entry-email-addresses"
				>
					From: {entry.data.email_from} → To: {entry.data.email_to}
				</Typography>
			)}
			{(entry.type === "email_queued" || entry.type === "email_sent") &&
				expanded &&
				entry.data.recipient_email && (
					<Typography
						variant="caption"
						color="text.secondary"
						sx={{ display: "block", mt: 0.25 }}
						data-component="entry-email-addresses"
					>
						To: {entry.data.recipient_email}
					</Typography>
				)}

			{/* Attachment row: name opens inline viewer; View + Download + Delete */}
			{entry.type === "attachment" && (
				<Stack
					direction="row"
					alignItems="center"
					spacing={0.5}
					sx={{ mt: 0.5 }}
					data-component="attachment-row"
				>
					<Link
						component="button"
						type="button"
						onClick={() =>
							entry.data.id !== undefined &&
							onAttachmentView?.({
								name: entry.data.name,
								type: entry.data.type,
								downloadUrl: `/api/v2/person_attachment/${entry.data.id}/download/`,
							})
						}
						sx={{
							flex: 1,
							textAlign: "left",
							fontSize: "0.875rem",
							fontWeight: 500,
							overflow: "hidden",
							textOverflow: "ellipsis",
							whiteSpace: "nowrap",
						}}
						data-component="attachment-link"
					>
						{entry.data.name || "(no name)"}
					</Link>
					<Tooltip title="View inline">
						<IconButton
							size="small"
							onClick={() =>
								entry.data.id !== undefined &&
								onAttachmentView?.({
									name: entry.data.name,
									type: entry.data.type,
									downloadUrl: `/api/v2/person_attachment/${entry.data.id}/download/`,
								})
							}
							data-component="attachment-view-button"
						>
							<VisibilityIcon sx={{ fontSize: 16 }} />
						</IconButton>
					</Tooltip>
					<Tooltip title="Download">
						<IconButton
							size="small"
							onClick={() =>
								entry.data.id !== undefined &&
								triggerAttachmentDownload(
									entry.data.id,
									entry.data.name
								)
							}
							data-component="attachment-download-button"
						>
							<DownloadIcon sx={{ fontSize: 16 }} />
						</IconButton>
					</Tooltip>
					{onAttachmentDelete && (
						<Tooltip title="Delete attachment">
							<IconButton
								size="small"
								color="error"
								onClick={() => onAttachmentDelete(entry.data)}
								data-component="attachment-delete-button"
							>
								<DeleteIcon sx={{ fontSize: 16 }} />
							</IconButton>
						</Tooltip>
					)}
				</Stack>
			)}

			{/* Admin-only storage debug for the uploaded file (self-gates on blob_debug) */}
			{entry.type === "attachment" && (
				<AttachmentBlobDebug attachment={entry.data} />
			)}

			{/* Entry text */}
			{text && entry.type !== "attachment" && (
				<Box
					onClick={() => isExpandable && setExpanded(!expanded)}
					sx={{ cursor: isExpandable ? "pointer" : "default", mt: 0.5 }}
					data-component="entry-text-container"
				>
					<Typography
						variant="body2"
						sx={{
							whiteSpace: "pre-line",
							overflowWrap: "anywhere",
							...(isExpandable &&
								!expanded && {
									overflow: "hidden",
									textOverflow: "ellipsis",
									display: "-webkit-box",
									WebkitLineClamp: 3,
									WebkitBoxOrient: "vertical",
								}),
						}}
						data-component="entry-text"
					>
						{text}
					</Typography>
					{isExpandable && (
						<Typography
							variant="caption"
							color="primary"
							sx={{ display: "block", mt: 0.25 }}
							data-component="entry-expand-toggle"
						>
							{expanded ? "Show less" : "Show more..."}
						</Typography>
					)}
				</Box>
			)}

			{/* What the message carried — one chip per document, so the recruiter
			    can see afterwards what this candidate actually received. */}
			{(entry.type === "email_queued" ||
				entry.type === "email_sent" ||
				entry.type === "linkedin_queued" ||
				entry.type === "linkedin_sent") &&
				(entry.data.documents_detail?.length ?? 0) > 0 && (
					<Box
						sx={{ display: "flex", gap: 0.5, flexWrap: "wrap", mt: 0.75 }}
						data-component="entry-documents"
					>
						{entry.data.documents_detail?.map((document) => (
							<Chip
								key={document.id}
								data-component="entry-document-chip"
								icon={<AttachFileIcon />}
								label={document.name}
								size="small"
								variant="outlined"
								component="a"
								clickable
								href={messageDocumentDownloadUrl(document.id)}
								target="_blank"
								rel="noopener"
							/>
						))}
					</Box>
				)}

			{/* Assignment/template context for pitches */}
			{(entry.type === "email_queued" ||
				entry.type === "email_sent" ||
				entry.type === "linkedin_queued" ||
				entry.type === "linkedin_sent") &&
				(entry.data.assignment_name ||
					entry.data.pitch_source_name ||
					entry.data.template_name) && (
					<Typography
						variant="caption"
						color="text.secondary"
						sx={{ mt: 0.5, display: "block", fontStyle: "italic" }}
						data-component="entry-assignment"
					>
						{/* Who wrote it: the AI prompt version when one did, else
						    the template the pitch rode. */}
						{(entry.data.pitch_source_name || entry.data.template_name) && (
							<>
								Pitch:{" "}
								{entry.data.pitch_source_name ??
									entry.data.template_name}
							</>
						)}
						{(entry.data.pitch_source_name || entry.data.template_name) &&
							entry.data.assignment_name &&
							" · "}
						{entry.data.assignment_name && (
							<>Role: {entry.data.assignment_name}</>
						)}
					</Typography>
				)}

			{/* Reply buttons */}
			<ReplyButtons
				entry={entry}
				currentUsername={currentUsername}
				onReplyClick={onReplyClick}
			/>

			{/* Queue status */}
			{(() => {
				if (entry.type === "linkedin_queued" && entry.data.error) {
					return (
						<Typography
							variant="caption"
							color="error.main"
							sx={{ mt: 0.5, display: "block" }}
							data-component="queue-error"
						>
							Error: {entry.data.error}
						</Typography>
					);
				}
				if (entry.type === "email_queued" && entry.data.error) {
					return (
						<Typography
							variant="caption"
							color="error.main"
							sx={{ mt: 0.5, display: "block" }}
							data-component="queue-error"
						>
							SMTP error: {entry.data.error}
						</Typography>
					);
				}
				if (
					(entry.type === "email_queued" || entry.type === "email_sent") &&
					entry.data.bounced
				) {
					return (
						<Typography
							variant="caption"
							color="error.main"
							sx={{ mt: 0.5, display: "block" }}
							data-component="queue-bounced"
						>
							Bounced
						</Typography>
					);
				}
				if (entry.type === "email_queued" || entry.type === "linkedin_queued") {
					return (
						<Typography
							variant="caption"
							color="warning.main"
							sx={{ mt: 0.5, display: "block" }}
							data-component="queue-pending"
						>
							Awaiting delivery...
						</Typography>
					);
				}
				return null;
			})()}
		</Card>
	);
};

// Sub-components to reduce main component size
const EmailStatusIcons = ({
	entry,
	showDebugIds,
	onDebugClick,
}: {
	entry: ActivityEntry & { type: "email_journal" };
	showDebugIds: boolean;
	onDebugClick: (email: PersonEmailMessage) => void;
}) => (
	<>
		{entry.data.has_tracking ? (
			entry.data.opened ? (
				<Tooltip title="Email Opened">
					<DraftsIcon
						sx={{ fontSize: 16, color: (t) => t.palette.success.main }}
						data-component="email-opened-icon"
					/>
				</Tooltip>
			) : (
				<Tooltip title="Unopened">
					<MarkunreadIcon
						sx={{ fontSize: 16 }}
						data-component="email-unopened-icon"
					/>
				</Tooltip>
			)
		) : null}
		{entry.data.bounce && (
			<Tooltip title="Email Delivery Failed">
				<ReportIcon
					sx={{ fontSize: 16, color: (t) => t.palette.error.main }}
					data-component="email-failed-icon"
				/>
			</Tooltip>
		)}
		{entry.data.has_bounced && (
			<Tooltip title="Email Delivery bounced">
				<SmsFailedIcon
					sx={{ fontSize: 16, color: (t) => t.palette.error.main }}
					data-component="email-bounced-icon"
				/>
			</Tooltip>
		)}
		{entry.data.auto_submitted && (
			<Tooltip title="Automatic Response">
				<SmartToyIcon sx={{ fontSize: 16 }} data-component="email-auto-icon" />
			</Tooltip>
		)}
		{showDebugIds && (
			<Tooltip title="Open Debug View">
				<IconButton
					size="small"
					onClick={(e) => {
						e.stopPropagation();
						onDebugClick(entry.data);
					}}
					sx={{ p: 0.25 }}
					data-component="email-debug-button"
				>
					<BugReportIcon sx={{ fontSize: 16 }} />
				</IconButton>
			</Tooltip>
		)}
	</>
);

const EmailQueueStatusIcons = ({
	entry,
	showDebugIds,
}: {
	entry: ActivityEntry & { type: "email_queued" | "email_sent" };
	showDebugIds: boolean;
}) => (
	<>
		{entry.data.has_tracking ? (
			entry.data.opened ? (
				<Tooltip title="Email Opened">
					<DraftsIcon
						sx={{ fontSize: 16, color: (t) => t.palette.success.main }}
						data-component="email-opened-icon"
					/>
				</Tooltip>
			) : (
				<Tooltip title="Unopened">
					<MarkunreadIcon
						sx={{ fontSize: 16 }}
						data-component="email-unopened-icon"
					/>
				</Tooltip>
			)
		) : null}
		{showDebugIds && (
			<Tooltip title="Debug Mode Active">
				<BugReportIcon
					sx={{ fontSize: 16 }}
					data-component="email-debug-icon"
				/>
			</Tooltip>
		)}
	</>
);

const ReplyButtons = ({
	entry,
	currentUsername,
	onReplyClick,
}: {
	entry: ActivityEntry;
	currentUsername?: string;
	onReplyClick?: (context: ReplyContext) => void;
}) => {
	// Email journal entries - show Reply button (received only) and Open in Mail button (both)
	if (
		entry.type === "email_journal" &&
		currentUsername &&
		entry.data.username === currentUsername
	) {
		const isSent = entry.data.folder_type === 1; // 1 = SENT
		const handleReplyClick = () => {
			if (onReplyClick) {
				onReplyClick(createEmailReplyContext(entry.data));
			}
		};

		return (
			<Box sx={{ mt: 1, display: "flex", justifyContent: "flex-end", gap: 1 }}>
				{!isSent && (
					<Button
						variant="outlined"
						size="small"
						onClick={handleReplyClick}
						startIcon={<ReplyIcon />}
						data-component="reply-email-button"
					>
						Reply
					</Button>
				)}
				{entry.data.computed_url && (
					<Button
						variant="outlined"
						size="small"
						href={entry.data.computed_url}
						target="_blank"
						data-component="open-email-button"
					>
						{entry.data.computed_url.includes("outlook.office365.com")
							? "Open in Outlook"
							: entry.data.computed_url.includes("mail.google.com")
								? "Open in Gmail"
								: "Open Mail"}
					</Button>
				)}
			</Box>
		);
	}

	// LinkedIn conversation entries - show Reply button and Reply on LinkedIn button
	if (
		entry.type === "linkedin_conversation" &&
		currentUsername &&
		entry.conversationUsername === currentUsername
	) {
		const handleReplyClick = () => {
			if (onReplyClick) {
				onReplyClick(
					createLinkedInReplyContext(
						entry.personName || "LinkedIn User",
						entry.date,
						entry.data.text || ""
					)
				);
			}
		};

		return (
			<Box sx={{ mt: 1, display: "flex", justifyContent: "flex-end", gap: 1 }}>
				<Button
					variant="outlined"
					size="small"
					onClick={handleReplyClick}
					startIcon={<ReplyIcon />}
					data-component="reply-linkedin-here-button"
				>
					Reply
				</Button>
				<Button
					variant="contained"
					size="small"
					href={
						entry.conversationType === "LINKEDIN"
							? `https://www.linkedin.com/messaging/thread/${entry.conversationShortUrn}/`
							: `https://www.linkedin.com/talent/inbox/0/main/id/${entry.conversationShortUrn}`
					}
					target="_blank"
					data-component="reply-linkedin-external-button"
				>
					Open on LinkedIn
				</Button>
			</Box>
		);
	}

	// Sent pitch (email_sent) - show Open in Mail button if the journal URL is available
	if (entry.type === "email_sent" && entry.data.computed_url) {
		return (
			<Box sx={{ mt: 1, display: "flex", justifyContent: "flex-end", gap: 1 }}>
				<Button
					variant="outlined"
					size="small"
					href={entry.data.computed_url}
					target="_blank"
					data-component="open-email-button"
				>
					{entry.data.computed_url.includes("outlook.office365.com")
						? "Open in Outlook"
						: entry.data.computed_url.includes("mail.google.com")
							? "Open in Gmail"
							: "Open Mail"}
				</Button>
			</Box>
		);
	}

	return null;
};
